import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAdminAuthenticated, logoutAdmin } from '@/lib/adminAuth';
import {
  PlanKind,
  PlanDay,
  PlanFrame,
  PlanSticker,
  StickerType,
  ApprovalStatus,
  APPROVAL_STATUSES,
  STICKER_TYPES,
  fetchPlan,
  createDay,
  updateDay,
  deleteDay,
  createFrame,
  updateFrame,
  deleteFrame,
  reorderFrames,
  reorderDays,
  uploadPlannerImage,
  seedDefaultPlan,
} from '@/lib/contentPlanner';
import DeliverablesPanel from '@/components/zera/DeliverablesPanel';
import CaptionSuggestModal from '@/components/zera/CaptionSuggestModal';
import BulkAIGenerateModal from '@/components/zera/BulkAIGenerateModal';
import AutoScheduleModal from '@/components/zera/AutoScheduleModal';

import {
  LogOut,
  Plus,
  Trash2,
  GripVertical,
  Upload,
  Save,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Grid3x3,
  Sparkles,
  Compass,
  RefreshCw,
  Eye,
  X,
  Clock,
} from 'lucide-react';


const statusColor: Record<ApprovalStatus, string> = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-300',
  'Review Approved': 'bg-sky-50 text-sky-700 border-sky-300',
  Rejected: 'bg-rose-50 text-rose-700 border-rose-300',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  Scheduled: 'bg-[#C9A961]/15 text-[#8B6F47] border-[#C9A961]/40',
};

const ContentPlanner: React.FC = () => {
  const navigate = useNavigate();
  const [kind, setKind] = useState<PlanKind>('feed');
  const [days, setDays] = useState<PlanDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [bulkAIOpen, setBulkAIOpen] = useState(false);
  const [autoScheduleOpen, setAutoScheduleOpen] = useState(false);


  useEffect(() => {
    if (!isAdminAuthenticated()) navigate('/mtnadmin', { replace: true });
  }, [navigate]);

  const loadAll = async () => {
    setLoading(true);
    const data = await fetchPlan(kind);
    setDays(data);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const handleSeed = async () => {
    if (!confirm('Seed the database with the default 7-day plan? Only do this on an empty plan.')) return;
    setSeeding(true);
    await seedDefaultPlan();
    setSeeding(false);
    await loadAll();
  };

  const handleAddDay = async () => {
    const newId = await createDay(kind, days.length);
    if (newId) {
      await loadAll();
      setExpandedDay(newId);
    }
  };

  const handleDeleteDay = async (id: string) => {
    if (!confirm('Delete this entire day? This cannot be undone.')) return;
    await deleteDay(id);
    await loadAll();
  };

  const handleDayPatch = async (id: string, patch: Partial<PlanDay>) => {
    setSaving(id);
    await updateDay(id, patch);
    setDays((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    setSaving(null);
  };

  // ---- Day reorder (drag and drop) ----
  const dragDayId = useRef<string | null>(null);
  const dragOverDayId = useRef<string | null>(null);

  const handleDayDrop = async () => {
    if (!dragDayId.current || !dragOverDayId.current || dragDayId.current === dragOverDayId.current) return;
    const from = days.findIndex((d) => d.id === dragDayId.current);
    const to = days.findIndex((d) => d.id === dragOverDayId.current);
    if (from < 0 || to < 0) return;
    const next = [...days];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDays(next);
    await reorderDays(kind, next.map((d) => d.id));
    dragDayId.current = null;
    dragOverDayId.current = null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0D1B2A] border-b border-[#C9A961]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#C9A961]" />
            </div>
            <div>
              <h1 className="font-serif text-lg text-white font-light">
                Content Planner <span className="text-[#C9A961] text-xs tracking-widest uppercase ml-2">Instagram</span>
              </h1>
              <p className="font-sans text-[10px] text-white/40 tracking-wider">
                Edit feed posts &amp; stories — changes publish instantly to /instagram-content
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/instagram-content"
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-white/15 text-white/70 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors flex items-center gap-2"
            >
              <Eye className="w-3.5 h-3.5" /> View Live
            </a>
            <button
              onClick={() => navigate('/mtnadmin/dashboard')}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-white/15 text-white/70 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
            >
              ← CRM
            </button>
            <button
              onClick={() => {
                logoutAdmin();
                navigate('/mtnadmin', { replace: true });
              }}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-[#C9A961]/10 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0D1B2A] transition-colors flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Tab switcher */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setKind('feed')}
              className={`inline-flex items-center gap-2 px-5 py-2.5 border font-sans text-[11px] tracking-[0.2em] uppercase transition-all ${
                kind === 'feed'
                  ? 'bg-[#1A3B5C] text-white border-[#1A3B5C]'
                  : 'bg-white text-[#1A3B5C] border-slate-200 hover:border-[#C9A961]'
              }`}
            >
              <Grid3x3 className="w-4 h-4" /> Feed Posts
            </button>
            <button
              onClick={() => setKind('stories')}
              className={`inline-flex items-center gap-2 px-5 py-2.5 border font-sans text-[11px] tracking-[0.2em] uppercase transition-all ${
                kind === 'stories'
                  ? 'bg-[#1A3B5C] text-white border-[#1A3B5C]'
                  : 'bg-white text-[#1A3B5C] border-slate-200 hover:border-[#C9A961]'
              }`}
            >
              <Sparkles className="w-4 h-4" /> Stories This Week
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {kind === 'feed' && days.length > 0 && (
              <>
                <button
                  onClick={() => setBulkAIOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] transition-all shadow-sm hover:shadow-md group"
                  title="Auto-fill every draft post's caption + 30 hashtags with AI"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#C9A961] group-hover:text-white transition-colors" />
                  Generate Week with AI
                </button>
                <button
                  onClick={() => setAutoScheduleOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-[#C9A961] bg-white text-[#8B6F47] font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#C9A961] hover:text-white transition-all shadow-sm hover:shadow-md"
                  title="AI suggests optimal post_time for every draft post based on type & theme"
                >
                  <Clock className="w-3.5 h-3.5" />
                  Auto-Schedule
                </button>
              </>
            )}

            <button
              onClick={loadAll}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-600 font-sans text-[11px] tracking-[0.2em] uppercase hover:border-[#C9A961] hover:text-[#C9A961] transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            {days.length === 0 && !loading && (
              <button
                onClick={handleSeed}
                disabled={seeding}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#C9A961]/40 bg-[#C9A961]/10 text-[#8B6F47] font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#C9A961] hover:text-white transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" /> {seeding ? 'Seeding...' : 'Seed Default Plan'}
              </button>
            )}
            <button
              onClick={handleAddDay}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A3B5C] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add Day
            </button>
          </div>

        </div>

        {/* Deliverables panel — actual social-media exports */}
        {!loading && days.length > 0 && <DeliverablesPanel days={days} kind={kind} />}

        {/* Days list */}
        {loading ? (
          <div className="py-24 text-center font-sans text-sm text-slate-400">Loading...</div>
        ) : days.length === 0 ? (
          <div className="bg-white border border-slate-200 py-20 text-center">
            <p className="font-sans text-sm text-slate-500 mb-4">
              No content for this tab yet.
            </p>
            <p className="font-sans text-xs text-slate-400 mb-6">
              Seed the default 7-day plan to get started, or click "Add Day".
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {days.map((day) => (
              <DayCard
                key={day.id}
                day={day}
                kind={kind}
                expanded={expandedDay === day.id}
                onToggle={() => setExpandedDay(expandedDay === day.id ? null : day.id)}
                onPatch={(patch) => handleDayPatch(day.id, patch)}
                onDelete={() => handleDeleteDay(day.id)}
                onFramesChanged={loadAll}
                saving={saving === day.id}
                onDragStart={() => (dragDayId.current = day.id)}
                onDragOver={(e) => {
                  e.preventDefault();
                  dragOverDayId.current = day.id;
                }}
                onDrop={handleDayDrop}
              />
            ))}
          </div>
        )}
      </div>

      <BulkAIGenerateModal
        open={bulkAIOpen}
        onClose={() => setBulkAIOpen(false)}
        days={days}
        onUpdated={loadAll}
      />

      <AutoScheduleModal
        open={autoScheduleOpen}
        onClose={() => setAutoScheduleOpen(false)}
        days={days}
        onUpdated={loadAll}
      />
    </div>
  );
};



// ============= DayCard =============

interface DayCardProps {
  day: PlanDay;
  kind: PlanKind;
  expanded: boolean;
  saving: boolean;
  onToggle: () => void;
  onPatch: (patch: Partial<PlanDay>) => void;
  onDelete: () => void;
  onFramesChanged: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
}

const DayCard: React.FC<DayCardProps> = ({
  day,
  kind,
  expanded,
  saving,
  onToggle,
  onPatch,
  onDelete,
  onFramesChanged,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const [localDay, setLocalDay] = useState(day);
  const [frames, setFrames] = useState<PlanFrame[]>(day.frames);

  useEffect(() => {
    setLocalDay(day);
    setFrames(day.frames);
  }, [day]);

  const commitField = (field: keyof PlanDay, value: any) => {
    onPatch({ [field]: value } as Partial<PlanDay>);
  };

  // Frame reorder
  const dragFrameId = useRef<string | null>(null);
  const dragOverFrameId = useRef<string | null>(null);

  const handleFrameDrop = async () => {
    if (!dragFrameId.current || !dragOverFrameId.current || dragFrameId.current === dragOverFrameId.current) return;
    const from = frames.findIndex((f) => f.id === dragFrameId.current);
    const to = frames.findIndex((f) => f.id === dragOverFrameId.current);
    if (from < 0 || to < 0) return;
    const next = [...frames];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setFrames(next);
    await reorderFrames(day.id, next.map((f) => f.id));
    dragFrameId.current = null;
    dragOverFrameId.current = null;
  };

  const handleAddFrame = async () => {
    const newId = await createFrame(day.id, frames.length, kind);
    if (newId) onFramesChanged();
  };

  const handleDeleteFrame = async (id: string) => {
    if (!confirm('Delete this frame?')) return;
    await deleteFrame(id);
    onFramesChanged();
  };

  const handleFramePatch = async (id: string, patch: Partial<PlanFrame>) => {
    await updateFrame(id, patch);
    setFrames((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const isFeed = kind === 'feed';

  return (
    <div
      className="bg-white border border-slate-200"
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Day header row */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-100">
        <GripVertical className="w-4 h-4 text-slate-300 cursor-grab" />
        <button onClick={onToggle} className="flex-shrink-0">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
        </button>

        <input
          value={localDay.day_label}
          onChange={(e) => setLocalDay({ ...localDay, day_label: e.target.value })}
          onBlur={() => commitField('day_label', localDay.day_label)}
          placeholder="Day name (e.g. Monday)"
          className="font-serif text-lg text-[#1A3B5C] bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#C9A961] outline-none px-1 py-1 min-w-[140px]"
        />
        <input
          value={localDay.date_label}
          onChange={(e) => setLocalDay({ ...localDay, date_label: e.target.value })}
          onBlur={() => commitField('date_label', localDay.date_label)}
          placeholder="Week 1 · Day 1"
          className="font-sans text-[11px] tracking-wider uppercase text-[#8B6F47] bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#C9A961] outline-none px-1 py-1 min-w-[120px]"
        />
        <input
          value={localDay.theme}
          onChange={(e) => setLocalDay({ ...localDay, theme: e.target.value })}
          onBlur={() => commitField('theme', localDay.theme)}
          placeholder="Theme"
          className="flex-1 font-sans text-sm text-slate-700 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#C9A961] outline-none px-1 py-1"
        />

        <select
          value={localDay.approval_status}
          onChange={(e) => {
            const v = e.target.value as ApprovalStatus;
            setLocalDay({ ...localDay, approval_status: v });
            commitField('approval_status', v);
          }}
          className={`font-sans text-[10px] tracking-wider uppercase px-2 py-1 border ${statusColor[localDay.approval_status]}`}
        >
          {APPROVAL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <span className="font-sans text-[10px] tracking-wider uppercase text-slate-400 px-2">
          {frames.length} {frames.length === 1 ? 'frame' : 'frames'}
        </span>

        {saving && <RefreshCw className="w-3.5 h-3.5 text-[#C9A961] animate-spin" />}

        <button
          onClick={onDelete}
          className="p-2 text-slate-400 hover:text-red-500 transition-colors"
          title="Delete day"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="p-5 bg-slate-50/50 space-y-4">
          {/* Narrative (stories only) */}
          {kind === 'stories' && (
            <div>
              <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                Day Narrative
              </label>
              <textarea
                value={localDay.narrative}
                onChange={(e) => setLocalDay({ ...localDay, narrative: e.target.value })}
                onBlur={() => commitField('narrative', localDay.narrative)}
                rows={2}
                placeholder="A short strategic note about this day's story arc..."
                className="w-full bg-white border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none"
              />
            </div>
          )}

          {/* Frames */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#1A3B5C] font-semibold">
                {isFeed ? 'Post' : 'Story Frames'}
              </h4>
              {!isFeed && (
                <button
                  onClick={handleAddFrame}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#C9A961]/40 bg-[#C9A961]/5 text-[#8B6F47] font-sans text-[10px] tracking-wider uppercase hover:bg-[#C9A961] hover:text-white transition-colors"
                >
                  <Plus className="w-3 h-3" /> Add Frame
                </button>
              )}
              {isFeed && frames.length === 0 && (
                <button
                  onClick={handleAddFrame}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#C9A961]/40 bg-[#C9A961]/5 text-[#8B6F47] font-sans text-[10px] tracking-wider uppercase hover:bg-[#C9A961] hover:text-white transition-colors"
                >
                  <Plus className="w-3 h-3" /> Add Post
                </button>
              )}
            </div>

            {frames.map((frame) => (
              <FrameEditor
                key={frame.id}
                frame={frame}
                kind={kind}
                dayLabel={localDay.day_label}
                dayTheme={localDay.theme}
                onPatch={(patch) => handleFramePatch(frame.id, patch)}
                onDelete={() => handleDeleteFrame(frame.id)}
                onDragStart={() => (dragFrameId.current = frame.id)}
                onDragOver={(e) => {
                  e.preventDefault();
                  dragOverFrameId.current = frame.id;
                }}
                onDrop={handleFrameDrop}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============= FrameEditor =============

interface FrameEditorProps {
  frame: PlanFrame;
  kind: PlanKind;
  dayLabel?: string;
  dayTheme?: string;
  onPatch: (patch: Partial<PlanFrame>) => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
}

const FrameEditor: React.FC<FrameEditorProps> = ({
  frame,
  kind,
  dayLabel = '',
  dayTheme = '',
  onPatch,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const [local, setLocal] = useState(frame);
  const [uploading, setUploading] = useState(false);
  const [hashtagInput, setHashtagInput] = useState(frame.hashtags.join(' '));
  const [aiOpen, setAiOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocal(frame);
    setHashtagInput(frame.hashtags.join(' '));
  }, [frame]);

  const commit = (patch: Partial<PlanFrame>) => {
    setLocal((p) => ({ ...p, ...patch }));
    onPatch(patch);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const url = await uploadPlannerImage(file);
    setUploading(false);
    if (url) commit({ image: url });
    e.target.value = '';
  };

  const handleHashtagsBlur = () => {
    const tags = hashtagInput
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.startsWith('#') ? t : `#${t}`));
    commit({ hashtags: tags });
  };

  // Sticker handling
  const updateSticker = (idx: number, patch: Partial<PlanSticker>) => {
    const next = local.stickers.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    commit({ stickers: next });
  };
  const addSticker = () => {
    commit({ stickers: [...local.stickers, { type: 'poll', label: '', detail: '' }] });
  };
  const removeSticker = (idx: number) => {
    commit({ stickers: local.stickers.filter((_, i) => i !== idx) });
  };

  const isFeed = kind === 'feed';

  return (
    <div
      className="bg-white border border-slate-200 p-4"
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="flex gap-4">
        {/* Drag handle + image */}
        <div className="flex flex-col items-center gap-2">
          <GripVertical className="w-4 h-4 text-slate-300 cursor-grab mt-1" />
          <div
            className="relative bg-slate-100 border border-slate-200 overflow-hidden"
            style={{ width: 90, aspectRatio: isFeed ? '1 / 1' : '9 / 16' }}
          >
            {local.image ? (
              <img src={local.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-slate-300" />
              </div>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="font-sans text-[9px] tracking-wider uppercase text-[#1A3B5C] hover:text-[#C9A961] flex items-center gap-1"
          >
            <Upload className="w-3 h-3" /> {uploading ? '...' : 'Upload'}
          </button>
        </div>

        {/* Fields */}
        <div className="flex-1 space-y-3 min-w-0">
          {/* Top row: status + delete */}
          <div className="flex items-center justify-between gap-3">
            <select
              value={local.approval_status}
              onChange={(e) => commit({ approval_status: e.target.value as ApprovalStatus })}
              className={`font-sans text-[10px] tracking-wider uppercase px-2 py-1 border ${statusColor[local.approval_status]}`}
            >
              {APPROVAL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              onClick={onDelete}
              className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
              title="Delete frame"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Image URL field */}
          <input
            value={local.image}
            onChange={(e) => setLocal({ ...local, image: e.target.value })}
            onBlur={() => commit({ image: local.image })}
            placeholder="Image URL (or upload above)"
            className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-xs focus:border-[#C9A961] outline-none"
          />

          {isFeed ? (
            <FeedFields
              local={local}
              setLocal={setLocal}
              commit={commit}
              hashtagInput={hashtagInput}
              setHashtagInput={setHashtagInput}
              handleHashtagsBlur={handleHashtagsBlur}
              onOpenAI={() => setAiOpen(true)}
            />
          ) : (
            <StoryFields
              local={local}
              setLocal={setLocal}
              commit={commit}
              stickers={local.stickers}
              updateSticker={updateSticker}
              addSticker={addSticker}
              removeSticker={removeSticker}
            />
          )}
        </div>
      </div>

      {isFeed && (
        <CaptionSuggestModal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          theme={dayTheme}
          imageUrl={local.image}
          dayLabel={dayLabel}
          postType={local.post_type}
          existingCaption={local.caption}
          onInsertCaption={(caption) => {
            setLocal((p) => ({ ...p, caption }));
            commit({ caption });
          }}
          onInsertHashtags={(tags) => {
            setHashtagInput(tags.join(' '));
            commit({ hashtags: tags });
          }}
        />
      )}
    </div>
  );
};

// ============= FeedFields =============

interface FeedFieldsProps {
  local: PlanFrame;
  setLocal: React.Dispatch<React.SetStateAction<PlanFrame>>;
  commit: (patch: Partial<PlanFrame>) => void;
  hashtagInput: string;
  setHashtagInput: (s: string) => void;
  handleHashtagsBlur: () => void;
  onOpenAI: () => void;
}

const FeedFields: React.FC<FeedFieldsProps> = ({
  local,
  setLocal,
  commit,
  hashtagInput,
  setHashtagInput,
  handleHashtagsBlur,
  onOpenAI,
}) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">Type</label>
        <select
          value={local.post_type}
          onChange={(e) => commit({ post_type: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-xs focus:border-[#C9A961] outline-none"
        >
          {['Feed Post', 'Reel', 'Carousel', 'Story'].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">Post Time</label>
        <input
          value={local.post_time}
          onChange={(e) => setLocal({ ...local, post_time: e.target.value })}
          onBlur={() => commit({ post_time: local.post_time })}
          placeholder="9:00 AM"
          className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-xs focus:border-[#C9A961] outline-none"
        />
      </div>
    </div>

    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500">
          Caption
        </label>
        <button
          type="button"
          onClick={onOpenAI}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] transition-all shadow-sm hover:shadow-md group"
          title="Generate 3 caption variants + 30 hashtags from the image and theme"
        >
          <Sparkles className="w-3 h-3 text-[#C9A961] group-hover:text-white transition-colors" />
          AI Suggest
        </button>
      </div>
      <textarea
        value={local.caption}
        onChange={(e) => setLocal({ ...local, caption: e.target.value })}
        onBlur={() => commit({ caption: local.caption })}
        rows={4}
        placeholder="The full caption — or click AI Suggest above to generate 3 variants from the image & theme."
        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none whitespace-pre-wrap"
      />
      <div className="font-sans text-[10px] text-slate-400 mt-1">
        {local.caption.length} chars · {local.caption.split(/\s+/).filter(Boolean).length} words
      </div>
    </div>

    <div>
      <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">CTA</label>
      <input
        value={local.cta}
        onChange={(e) => setLocal({ ...local, cta: e.target.value })}
        onBlur={() => commit({ cta: local.cta })}
        placeholder="Tap the link in bio..."
        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none"
      />
    </div>

    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500">
          Hashtags (space-separated)
        </label>
        <span className="font-sans text-[10px] text-slate-400">
          {hashtagInput.split(/\s+/).filter(Boolean).length} tags
        </span>
      </div>
      <input
        value={hashtagInput}
        onChange={(e) => setHashtagInput(e.target.value)}
        onBlur={handleHashtagsBlur}
        placeholder="#ZeraTravel #AlbanianRiviera"
        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-xs focus:border-[#C9A961] outline-none"
      />
    </div>
  </>
);

// ============= StoryFields =============

interface StoryFieldsProps {
  local: PlanFrame;
  setLocal: React.Dispatch<React.SetStateAction<PlanFrame>>;
  commit: (patch: Partial<PlanFrame>) => void;
  stickers: PlanSticker[];
  updateSticker: (idx: number, patch: Partial<PlanSticker>) => void;
  addSticker: () => void;
  removeSticker: (idx: number) => void;
}

const StoryFields: React.FC<StoryFieldsProps> = ({
  local,
  setLocal,
  commit,
  stickers,
  updateSticker,
  addSticker,
  removeSticker,
}) => (
  <>
    <div>
      <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">Headline</label>
      <input
        value={local.headline}
        onChange={(e) => setLocal({ ...local, headline: e.target.value })}
        onBlur={() => commit({ headline: local.headline })}
        placeholder="good morning, ionian"
        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-serif text-base focus:border-[#C9A961] outline-none"
      />
    </div>

    <div>
      <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">On-Screen Copy</label>
      <textarea
        value={local.copy}
        onChange={(e) => setLocal({ ...local, copy: e.target.value })}
        onBlur={() => commit({ copy: local.copy })}
        rows={2}
        placeholder="Short text shown on the story..."
        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none whitespace-pre-wrap"
      />
    </div>

    <div>
      <label className="block font-sans text-[9px] tracking-wider uppercase text-slate-500 mb-1">
        Link Sticker Destination
      </label>
      <input
        value={local.link_destination}
        onChange={(e) => setLocal({ ...local, link_destination: e.target.value })}
        onBlur={() => commit({ link_destination: local.link_destination })}
        placeholder="/yacht-charters"
        className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-xs focus:border-[#C9A961] outline-none"
      />
    </div>

    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="font-sans text-[9px] tracking-wider uppercase text-slate-500">Stickers</label>
        <button
          onClick={addSticker}
          className="inline-flex items-center gap-1 font-sans text-[10px] tracking-wider uppercase text-[#1A3B5C] hover:text-[#C9A961]"
        >
          <Plus className="w-3 h-3" /> Add Sticker
        </button>
      </div>
      <div className="space-y-2">
        {stickers.map((s, idx) => (
          <div key={idx} className="grid grid-cols-[110px_1fr_1fr_auto] gap-2 items-center bg-slate-50 border border-slate-200 p-2">
            <select
              value={s.type}
              onChange={(e) => updateSticker(idx, { type: e.target.value as StickerType })}
              className="bg-white border border-slate-200 px-2 py-1.5 font-sans text-xs focus:border-[#C9A961] outline-none"
            >
              {STICKER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              value={s.label}
              onChange={(e) => updateSticker(idx, { label: e.target.value })}
              placeholder="Sticker label"
              className="bg-white border border-slate-200 px-2 py-1.5 font-sans text-xs focus:border-[#C9A961] outline-none"
            />
            <input
              value={s.detail || ''}
              onChange={(e) => updateSticker(idx, { detail: e.target.value })}
              placeholder="Detail (e.g. options A / B)"
              className="bg-white border border-slate-200 px-2 py-1.5 font-sans text-xs focus:border-[#C9A961] outline-none"
            />
            <button
              onClick={() => removeSticker(idx)}
              className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {!stickers.length && (
          <div className="text-center py-3 font-sans text-xs text-slate-400 italic">No stickers yet.</div>
        )}
      </div>
    </div>
  </>
);

export default ContentPlanner;
